import { useEffect, useRef, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

// Draws the Agriware logomark to a canvas and swaps the favicon href. When
// `alert` is true the mark pulses, so a blocked machine is visible from the tab
// without switching to it. Browsers control tab width, so nothing here (or
// anywhere in a page) can stop tabs shrinking as more are opened.

const SIZE = 64;
const PERIOD = 2400;

// Watches for blocked machines so the tab pulses only when something needs
// attention. A constantly animated favicon becomes wallpaper; one that moves
// only when a machine is stopped is a signal worth glancing at.
function useBlockedFleet() {
  const { orgId } = useAuth();
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    if (!orgId) { setBlocked(false); return; }
    const q = query(
      collection(db, 'organisations', orgId, 'machines'),
      where('isBlocked', '==', true)
    );
    const unsub = onSnapshot(
      q,
      (snap) => setBlocked(!snap.empty),
      () => setBlocked(false)
    );
    return unsub;
  }, [orgId]);

  return blocked;
}

export default function BrandFavicon({ title = 'Agriware' }) {
  const alert = useBlockedFleet();
  const imgRef = useRef(null);
  const frameRef = useRef(null);
  const linkRef = useRef(null);

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    linkRef.current = link;

    const img = new Image();
    img.src = '/logomark.png';
    img.onload = () => { imgRef.current = img; start(); };

    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');

    const draw = (opacity) => {
      const image = imgRef.current;
      if (!image) return;
      ctx.clearRect(0, 0, SIZE, SIZE);
      ctx.globalAlpha = opacity;
      // Contain the mark within the square, preserving its proportions.
      const scale = Math.min(SIZE / image.width, SIZE / image.height);
      const w = image.width * scale;
      const h = image.height * scale;
      ctx.drawImage(image, (SIZE - w) / 2, (SIZE - h) / 2, w, h);
      ctx.globalAlpha = 1;
      try { link.href = canvas.toDataURL('image/png'); } catch (e) { /* ignore */ }
    };

    const start = () => {
      if (!alert) { draw(1); return; }
      const tick = () => {
        const phase = (Date.now() % PERIOD) / PERIOD;
        // Ease between full and faint, never fully disappearing.
        const opacity = 0.35 + 0.65 * (0.5 + 0.5 * Math.cos(phase * 2 * Math.PI));
        draw(opacity);
        frameRef.current = setTimeout(tick, 120);
      };
      tick();
    };

    if (imgRef.current) start();

    return () => { if (frameRef.current) clearTimeout(frameRef.current); };
  }, [alert]);

  return null;
}
